/**
 * SuspensionContextMenu.tsx (GRE-113)
 *
 * Context menu for suspension connectors on the canvas.
 * Provides options to delete, change type, or adjust duration.
 */

import React from 'react';
import { ContextMenu, ContextMenuItem } from '@/components/UI/ContextMenu';
import type { SuspensionType, SuspensionConnector } from '@/types/suspension-connector';
import { SUSPENSION_CONFIGS } from '@/types/suspension-connector';

interface SuspensionContextMenuProps {
  isOpen: boolean;
  position: { x: number; y: number };
  connector: SuspensionConnector | null;
  onClose: () => void;
  onDelete: (id: string) => void;
  onChangeType: (id: string, newType: SuspensionType) => void;
  onChangeDuration: (id: string, newDuration: number) => void;
}

export const SuspensionContextMenu: React.FC<SuspensionContextMenuProps> = ({
  isOpen,
  position,
  connector,
  onClose,
  onDelete,
  onChangeType,
  onChangeDuration,
}) => {
  if (!connector) return null;

  const currentConfig = SUSPENSION_CONFIGS[connector.suspensionType];

  // Render colored dot for suspension type
  const renderTypeDot = (type: SuspensionType, isCurrent: boolean) => {
    const config = SUSPENSION_CONFIGS[type];
    return (
      <span
        style={{
          display: 'inline-block',
          width: '12px',
          height: '12px',
          borderRadius: '6px',
          backgroundColor: config.color,
          border: isCurrent ? '2px solid white' : 'none',
          boxShadow: isCurrent ? '0 0 0 1px rgba(0,0,0,0.3)' : 'none',
        }}
      />
    );
  };

  // Duration options (in beats)
  const durationOptions = [0.5, 1, 1.5, 2, 3, 4];

  const menuItems: ContextMenuItem[] = [
    // Header showing current suspension info
    {
      id: 'header',
      label: `${currentConfig.label} Suspension`,
      icon: renderTypeDot(connector.suspensionType, false),
      disabled: true,
    },

    { id: 'divider-1', label: '', divider: true },

    // Change suspension type submenu
    {
      id: 'change-type',
      label: 'Change Type',
      submenu: (Object.keys(SUSPENSION_CONFIGS) as SuspensionType[]).map((type) => {
        const config = SUSPENSION_CONFIGS[type];
        const isCurrent = type === connector.suspensionType;
        return {
          id: `type-${type}`,
          label: `${config.label}${isCurrent ? ' (current)' : ''}`,
          icon: renderTypeDot(type, isCurrent),
          action: () => {
            if (!isCurrent) {
              onChangeType(connector.id, type);
            }
          },
          disabled: isCurrent,
        };
      }),
    },

    // Change duration submenu
    {
      id: 'change-duration',
      label: 'Duration',
      submenu: durationOptions.map((beats) => {
        const isCurrent = connector.suspensionDuration === beats;
        const label = beats === 1 ? '1 beat' : `${beats} beats`;
        return {
          id: `duration-${beats}`,
          label: `${label}${isCurrent ? ' (current)' : ''}`,
          action: () => {
            if (!isCurrent) {
              onChangeDuration(connector.id, beats);
            }
          },
          disabled: isCurrent,
        };
      }),
    },

    { id: 'divider-2', label: '', divider: true },

    // Voice info (read-only)
    {
      id: 'voice-info',
      label: `Voice: ${connector.suspendedVoice}`,
      icon: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="8" cy="8" r="6" />
          <line x1="8" y1="5" x2="8" y2="8" />
          <line x1="8" y1="11" x2="8" y2="11.5" />
        </svg>
      ),
      disabled: true,
    },

    // Notes info (read-only)
    {
      id: 'notes-info',
      label: `${connector.suspensionNote} → ${connector.resolutionNote}`,
      icon: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M4 8h8M10 5l3 3-3 3" />
        </svg>
      ),
      disabled: true,
    },

    { id: 'divider-3', label: '', divider: true },

    // Delete action
    {
      id: 'delete',
      label: 'Delete Suspension',
      icon: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 5h10M5 5V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1M6 7v5M10 7v5" />
          <path d="M4 5l1 9h6l1-9" />
        </svg>
      ),
      action: () => onDelete(connector.id),
    },
  ];

  return (
    <ContextMenu
      isOpen={isOpen}
      position={position}
      items={menuItems}
      onClose={onClose}
    />
  );
};
